// ---------------------------------------------------------------------------
// Project-management board data layer (ported from the standalone "Monday v2"
// app). Lives in the hub's Firebase under SEPARATE, namespaced collections so
// it never touches the hub's own client/task data:
//   pmClients   — board clients   { name, board, website, contactName, notes, archived, createdAt }
//   pmTasks     — board tasks      { clientId, name, status, owner, date, notes, order, comments[] }
//   pmTemplates — task checklists  docs: 'seo' | 'website'  { tasks: [{id,name,defaultOwner}] }
//
// board values: 'website' | 'in-house' | 'seo' (seo is grouped with in-house).
// Queries avoid composite indexes by filtering/sorting client-side.
// ---------------------------------------------------------------------------
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, setDoc,
  onSnapshot, query, where, Timestamp, writeBatch,
} from 'firebase/firestore';
import { db, auth } from '../firebase';

// ── Clients ────────────────────────────────────────────────────────────────

// Subscribe to every board client (sorted by name). The caller filters by
// board + archived in-memory — keeps us off composite indexes and lets the
// board show its own archived list with a simple toggle.
export const subscribeToPmClients = (callback) => {
  const q = query(collection(db, 'pmClients'));
  return onSnapshot(q, (snap) => {
    const clients = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    callback(clients);
  }, (err) => console.error('pmClients snapshot error:', err));
};

// Does this client belong to the given board? 'in-house' also catches 'seo'.
export const clientOnBoard = (client, board) =>
  board === 'in-house' ? (client.board === 'in-house' || client.board === 'seo') : client.board === board;

export const createPmClient = async (data) => {
  const ref = await addDoc(collection(db, 'pmClients'), {
    name: data.name || '',
    website: data.website || '',
    contactName: data.contactName || '',
    notes: data.notes || '',
    board: data.board,
    archived: false,
    createdAt: Timestamp.now(),
  });
  return ref.id;
};

export const updatePmClient = async (clientId, data) => {
  await updateDoc(doc(db, 'pmClients', clientId), data);
};

export const deletePmClient = async (clientId) => {
  // Remove the client and all of its tasks together.
  const tasksSnap = await getDocs(query(collection(db, 'pmTasks'), where('clientId', '==', clientId)));
  const batch = writeBatch(db);
  tasksSnap.docs.forEach(d => batch.delete(d.ref));
  batch.delete(doc(db, 'pmClients', clientId));
  await batch.commit();
};

// ── Tasks ──────────────────────────────────────────────────────────────────

// Subscribe to a client's tasks. Sorted by `order` client-side (no composite index).
export const subscribeToPmTasks = (clientId, callback) => {
  const q = query(collection(db, 'pmTasks'), where('clientId', '==', clientId));
  return onSnapshot(q, (snap) => {
    const tasks = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    callback(tasks);
  }, (err) => console.error('pmTasks snapshot error:', err));
};

export const createPmTask = async (taskData) => {
  const ref = await addDoc(collection(db, 'pmTasks'), taskData);
  return ref.id;
};

export const updatePmTask = async (taskId, data) => {
  await updateDoc(doc(db, 'pmTasks', taskId), data);
};

export const deletePmTask = async (taskId) => {
  await deleteDoc(doc(db, 'pmTasks', taskId));
};

export const reorderPmTasks = async (tasks) => {
  const batch = writeBatch(db);
  tasks.forEach((task, index) => batch.update(doc(db, 'pmTasks', task.id), { order: index }));
  await batch.commit();
};

export const applyTemplateToPmClient = async (clientId, board) => {
  const templateId = (board === 'in-house' || board === 'seo') ? 'seo' : 'website';
  const templateDoc = await getDoc(doc(db, 'pmTemplates', templateId));
  if (!templateDoc.exists()) {
    throw new Error(`Template "${templateId}" not found. Open Setup and save it first.`);
  }
  const templateTasks = templateDoc.data().tasks || [];
  if (templateTasks.length === 0) {
    throw new Error(`Template "${templateId}" has no tasks. Add some in Setup.`);
  }
  const tasksSnap = await getDocs(query(collection(db, 'pmTasks'), where('clientId', '==', clientId)));
  const currentCount = tasksSnap.size;

  const batch = writeBatch(db);
  templateTasks.forEach((t, index) => {
    const taskRef = doc(collection(db, 'pmTasks'));
    batch.set(taskRef, {
      clientId,
      name: t.name,
      status: '',
      owner: t.defaultOwner || 'Phil',
      date: null,
      notes: '',
      order: currentCount + index,
    });
  });
  await batch.commit();
};

// ── Comments (stored on the task doc) ────────────────────────────────────────

export const addPmTaskComment = async (taskId, text) => {
  const user = auth.currentUser;
  if (!user) throw new Error('You must be signed in to comment.');
  const taskDoc = await getDoc(doc(db, 'pmTasks', taskId));
  if (!taskDoc.exists()) throw new Error('Task not found.');
  const comments = taskDoc.data().comments || [];
  const newComment = {
    id: Math.random().toString(36).substring(2, 9),
    userId: user.uid,
    userName: user.displayName || user.email || 'Anonymous',
    text,
    createdAt: Timestamp.now(),
  };
  await updateDoc(doc(db, 'pmTasks', taskId), { comments: [...comments, newComment] });
};

export const updatePmTaskComment = async (taskId, commentId, text) => {
  const taskDoc = await getDoc(doc(db, 'pmTasks', taskId));
  if (!taskDoc.exists()) throw new Error('Task not found.');
  const comments = taskDoc.data().comments || [];
  const updated = comments.map(c => c.id === commentId ? { ...c, text, updatedAt: Timestamp.now() } : c);
  await updateDoc(doc(db, 'pmTasks', taskId), { comments: updated });
};

export const deletePmTaskComment = async (taskId, commentId) => {
  const taskDoc = await getDoc(doc(db, 'pmTasks', taskId));
  if (!taskDoc.exists()) throw new Error('Task not found.');
  const comments = (taskDoc.data().comments || []).filter(c => c.id !== commentId);
  await updateDoc(doc(db, 'pmTasks', taskId), { comments });
};

// ── Templates ────────────────────────────────────────────────────────────────

export const DEFAULT_SEO_TASKS = [
  'Monthly Press Release', 'Monthly Report Summaries', 'Monthly Outrank.so Review',
  'Monthly Whiteboard Video', 'Weekly Facebook + GBP Post', 'GBP Products',
  'GBP Services', 'Client Info for Claude', 'Train Claude Project', 'Integrations',
  'Agency Reporting', 'Schema (home/service)', 'RankMath (home/services)',
  'Outrank', 'DLVR.IT', 'Bright Local', 'Onsite Optimization',
  'Google Reviews Feed/Badge', 'Postcards Ordered',
];

export const DEFAULT_WEBSITE_TASKS = [
  'Project Brief + Assets', 'Hosting Account Setup - Nexcess', 'Domain Registration / Login',
  'Website Content', 'Home Page Mock-up', 'One Service/Product Page Mock-up',
  'Send to Client for Review (round 1)', 'Implement Client Change Request (round 1)',
  'Send Homepage to Client for Re-review', 'Front-end Development', 'Back-End Development',
  'Responsive Implementation', 'Functionality Testing', 'Send Full Site to Client for Review',
  'Implement Client Change Request (round 2)', 'Send to Client for Review (round 3)',
  'Implement Client Change Request (round 3)', 'SEO Implementation',
  'Install Analytics + Site Kit by Google', 'Install ADA Widget',
  'Send to Client for Final Review', 'Launch / Go Live', 'Post-Launch Testing',
  'Submit Sitemap to Google', 'Project Complete / Handoff',
];

export const getPmTemplate = async (templateId) => {
  const snap = await getDoc(doc(db, 'pmTemplates', templateId));
  return snap.exists() ? (snap.data().tasks || []) : [];
};

export const savePmTemplate = async (templateId, tasks) => {
  await setDoc(doc(db, 'pmTemplates', templateId), { tasks });
};

// Seed the two default templates once, if they don't exist yet.
export const seedPmTemplates = async () => {
  try {
    const seoDoc = await getDoc(doc(db, 'pmTemplates', 'seo'));
    if (!seoDoc.exists()) {
      await setDoc(doc(db, 'pmTemplates', 'seo'), {
        tasks: DEFAULT_SEO_TASKS.map((name, i) => ({ id: `seo-${i}`, name, defaultOwner: 'Phil' })),
      });
    }
    const webDoc = await getDoc(doc(db, 'pmTemplates', 'website'));
    if (!webDoc.exists()) {
      await setDoc(doc(db, 'pmTemplates', 'website'), {
        tasks: DEFAULT_WEBSITE_TASKS.map((name, i) => ({ id: `web-${i}`, name, defaultOwner: 'Phil' })),
      });
    }
  } catch (err) {
    console.warn('PM template seeding skipped or failed:', err);
  }
};
